import type { CreditSummaryData, CreditSummary, LinkedAccount, PeriodSummary } from '../types/credits.types';
import { formatDate, formatDayMonthYear, formatDayDeMonth, formatMonthDay } from '@chikara/utils';

export function mapCreditSummaryData(data: CreditSummaryData): CreditSummary {
  const spentAmount = data?.credit_line - data?.available < 0 ? 0 : data?.credit_line - data?.available;
  return {
    creditLine: data?.credit_line || 0,
    spent: spentAmount,
    available: data?.available || 0,
    toPayAmount: data?.payment_obligation?.amount > 0 ? data?.payment_obligation?.amount : spentAmount,
    toPayDate: formatDayMonthYear(data?.payment_obligation?.deadline),
  };
}

export function mapLinkedAccount(data: CreditSummaryData): LinkedAccount {
  return {
    accountName: data?.deposit_info?.account_tag || 'Sin asignar',
    clabe: data?.deposit_info?.clabe || 'Sin asignar',
    bankName: data?.deposit_info?.bank_name || 'Sin asignar',
  };
}

export function mapPeriodSummary(data: CreditSummaryData): PeriodSummary {
  const getDaysUntil = (deadline: string): number => {
    const targetDate = new Date(deadline);
    const now = new Date();

    const utcTarget = Date.UTC(targetDate.getUTCFullYear(), targetDate.getUTCMonth(), targetDate.getUTCDate());
    const utcNow = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

    const diffMs = utcTarget - utcNow;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : 0;
  };

  return {
    periodStartDate: formatMonthDay(data?.period_breakdown?.start_date),
    paymentDueDate: formatDayDeMonth(data?.payment_obligation?.deadline),
    cutoffDate: formatMonthDay(data?.period_breakdown?.end_date),
    nextCutoffDate: formatDayDeMonth(data?.period_breakdown?.next_cutoff_day),
    balanceDate: formatDate(new Date().toISOString()),
    periodStartBalance: data?.period_breakdown?.opening_balance || 0,
    charges: data?.period_breakdown?.charges || 0,
    credits: data?.period_breakdown?.payments || 0,
    interestAndFees: data?.period_breakdown?.interest_fee || 0,
    currentBalance: data?.period_breakdown?.current_balance || 0,
    cutoffInDays: getDaysUntil(data?.period_breakdown?.end_date || 'Sin información'),
    progressPercentage: (() => {
      const start = new Date(data?.period_breakdown?.start_date).getTime();
      const end = new Date(data?.period_breakdown?.end_date).getTime();
      const now = new Date().getTime();

      if (isNaN(start) || isNaN(end) || start >= end) return 0;

      const progress = ((now - start) / (end - start)) * 100;
      return Math.min(Math.max(progress, 0), 100);
    })(),
  };
}
